export type Stage = 'SUPERVISOR' | 'ACCOUNTING' | 'ADMINISTRATION' | 'PRESIDENT';

export const STAGES: Stage[] = ['SUPERVISOR', 'ADMINISTRATION', 'ACCOUNTING', 'PRESIDENT'];
export const STAGE_LABEL: Record<Stage, string> = {
  SUPERVISOR: 'Supervisor',
  ACCOUNTING: 'Accounting',
  ADMINISTRATION: 'Administration',
  PRESIDENT: 'Final approval',
};

export interface StageInfo {
  stage: Stage;
  decision: 'Y' | 'N' | null;
  byName: string | null;
  date: string | null;
  at: string | null;
  note: string | null;
}

export interface BonusSummary {
  id: string;
  categoryId: string;
  categoryName: string;
  programId?: string | null;
  programName?: string | null;
  frequency: 'WEEKLY' | 'MONTHLY' | 'ONE_TIME' | 'PER_EVENT';
  periodStart: string;
  periodEnd: string;
  campaignId: string | null;
  campaignName: string;
  offeredBy: 'CUSTOMER' | 'IN_HOUSE';
  currency: string;
  concept: string | null;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  nextStage: Stage | null;
  /** The three first approvals are done and the final approval is still pending. */
  awaitingFinal?: boolean;
  stages: StageInfo[];
  customer: { approved: string | null; approvedDate: string | null; paid: string | null; paidDate: string | null };
  /** What the customer paid TEKS for this bonus (recorded by accounting). Separate from approvals and payroll. */
  funding: { state: FundingState; due: number; funded: number; outstanding: number };
  /** Paid only when payroll settled every earning. */
  settlement: { sent: number; settled: number; total: number; paid: boolean };
  employeeCount: number;
  earningCount?: number;
  total: number;
  includedInPayroll: boolean;
  allIncluded: boolean;
  hrSynced: boolean;
  createdByName: string | null;
  readyForPayroll: boolean;
}

export interface BonusLine {
  id: string;
  employeeId: string;
  employeeName: string;
  employeeNumber: string | null;
  amount: number;
  concept: string | null;
  status: 'ACTIVE' | 'REMOVED';
  removedByName: string | null;
  removedAt: string | null;
  removedReason: string | null;
  addedByName: string | null;
  includedInPayroll: boolean;
  /** Sent to payroll is not paid: only SETTLED means payroll has paid the period that includes it. */
  paymentState?: PaymentState;
  settledAt?: string | null;
  settlementRef?: string | null;
  reconStatus?: ReconStatus | null;
  reconNote?: string | null;
  isAdjustment?: boolean;
  isHistorical?: boolean;
  payrollRef: string | null;
  hrSyncedAt: string | null;
  /** Program earnings. */
  earningDate?: string | null;
  quantity?: number | null;
  unitRate?: number | null;
  calcMethod?: CalcMethod | null;
  reference?: string | null;
  ruleVersion?: number | null;
  explanation?: string | null;
  /** Held by an eligibility review that is not decided as eligible. */
  eligibility?: { status: FlagStatus; text: string } | null;
}

export type CalcMethod = 'FIXED' | 'QUANTITY_RATE' | 'MANUAL' | 'PERCENTAGE' | 'TIERED' | 'MILESTONE';
export const METHOD_LABEL: Record<CalcMethod, string> = {
  FIXED: 'Fixed amount per case',
  QUANTITY_RATE: 'Quantity x rate',
  MANUAL: 'Entered by the supervisor',
  PERCENTAGE: 'Percent of a base amount',
  TIERED: 'Levels by result',
  MILESTONE: 'Referral milestones after the hire',
};
export interface Tier { from: number; amount: number }
export interface Milestone { label: string; days: number; amount: number }
export interface RuleParams { amount?: number; rate?: number; percent?: number; tiers?: Tier[]; metricLabel?: string; milestones?: Milestone[] }
export const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** The rule in force for a program bonus, as the add-earning screen needs it. */
export interface ProgramInfo {
  id: string;
  name: string;
  method: CalcMethod | null;
  params: RuleParams;
  ruleVersion: number | null;
  duplicatePolicy: 'REFERENCE' | 'NONE';
  referenceLabel: string | null;
  evidenceRequired: boolean;
  minAmount: number | null;
  maxAmount: number | null;
  capPerEmployee: number | null;
}

export interface BonusProgram {
  id: string;
  categoryId: string;
  categoryName: string;
  campaignId: string | null;
  campaignName: string | null;
  name: string;
  description: string | null;
  active: boolean;
  effectiveFrom: string | null;
  effectiveTo: string | null;
  frequency: 'WEEKLY' | 'MONTHLY';
  periodEndWeekday: number | null;
  currency: string;
  offeredBy: 'CUSTOMER' | 'IN_HOUSE';
  minAmount: number | null;
  maxAmount: number | null;
  capPerEmployee: number | null;
  duplicatePolicy: 'REFERENCE' | 'NONE';
  referenceLabel: string | null;
  evidenceRequired: boolean;
  versions: Array<{ id: string; version: number; effectiveFrom: string; method: CalcMethod; params: RuleParams; note: string | null }>;
}

export interface BonusAttachment {
  id: string;
  kind: 'PROOF' | 'SCREENSHOT';
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  uploadedByName: string | null;
  createdAt: string;
}

export interface BonusEvent {
  lineId: string | null;
  action: string;
  stage: string | null;
  decision: string | null;
  actorName: string | null;
  actorRole: string | null;
  override: number;
  note: string | null;
  createdAt: string;
}

export interface BonusPermissions {
  canEdit: boolean;
  canAttach?: boolean;
  readOnlyReason: string | null;
  editClearsApprovals: boolean;
  canRemoveEmployee: boolean;
  actionableStage: Stage | null;
  /** The newest decision, which this user may cancel (approvals are cancelled newest first). */
  cancellableStage?: Stage | null;
  cancelBlockedReason?: string | null;
  blockedReason: string | null;
  canDelete: boolean;
}

export interface BonusDetail extends BonusSummary {
  program?: ProgramInfo | null;
  lines: BonusLine[];
  events: BonusEvent[];
  attachments?: BonusAttachment[];
  permissions?: BonusPermissions;
}

export interface BonusCategory {
  id: string;
  name: string;
  frequency: string;
  offeredBy: 'CUSTOMER' | 'IN_HOUSE';
  defaultAmount: number | null;
  currency: string;
  isSystem: number;
  active?: number;
}

export interface Campaign { id: string; name: string }
export interface EmployeeOption { id: string; firstName: string; lastName: string; employeeNumber?: string | null }

export const errText = (e: any): string => e?.response?.data?.message || e?.message || 'Request failed';
export const money = (n: number, currency = 'USD') =>
  `${new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0)} ${currency}`;
export const todayYmd = () => new Date().toISOString().slice(0, 10);
export const employeeLabel = (u: EmployeeOption) => `${u.firstName} ${u.lastName}${u.employeeNumber ? ` (${u.employeeNumber})` : ''}`;

export type FlagStatus = 'UNDER_REVIEW' | 'ELIGIBLE' | 'INELIGIBLE';
export const FLAG_LABEL: Record<FlagStatus, string> = { UNDER_REVIEW: 'Under review', ELIGIBLE: 'Eligible', INELIGIBLE: 'Not eligible' };

export interface EligibilityRuleInfo {
  id: string;
  name: string;
  description: string | null;
  programId: string | null;
  programName: string | null;
  effectiveFrom: string | null;
  effectiveTo: string | null;
  active: boolean;
}

export interface EligibilityFlagInfo {
  id: string;
  ruleId: string;
  ruleName: string;
  employeeId: string;
  employeeName: string;
  employeeNumber: string | null;
  startDate: string;
  endDate: string;
  note: string;
  source: 'MANUAL' | 'IMPORT';
  status: FlagStatus;
  decidedByName: string | null;
  decidedAt: string | null;
  decisionNote: string | null;
  createdByName: string | null;
  createdAt: string;
  evidenceCount: number;
  heldEarnings: number;
}

export interface EligibilityFlagDetail extends EligibilityFlagInfo {
  events: Array<{ action: string; actorName: string; note: string | null; createdAt: string }>;
  attachments: Array<{ id: string; fileName: string; mimeType: string; sizeBytes: number; uploadedByName: string | null; createdAt: string }>;
  heldLines: Array<{ id: string; amount: number; reference: string | null; earningDate: string; recordId: string; periodEnd: string; programName: string | null }>;
}

export type ReferralStatus = 'OPEN' | 'HIRED' | 'LEFT' | 'CANCELLED';
export const REFERRAL_LABEL: Record<ReferralStatus, string> = { OPEN: 'Waiting for the hire', HIRED: 'Hired', LEFT: 'Left', CANCELLED: 'Cancelled' };
export type MilestoneState = 'WAITING' | 'DUE' | 'CLAIMED' | 'LOST';
export const MILESTONE_LABEL: Record<MilestoneState, string> = { WAITING: 'Waiting', DUE: 'Due', CLAIMED: 'In a bonus', LOST: 'Lost' };

export interface ReferralInfo {
  id: string;
  code: string;
  programId: string;
  programName: string;
  referrerId: string;
  referrerName: string;
  referrerNumber: string | null;
  candidateName: string;
  referredUserId: string | null;
  referredUserName: string | null;
  status: ReferralStatus;
  hireDate: string | null;
  endDate: string | null;
  note: string | null;
  createdByName: string | null;
  createdAt: string;
  currency: string;
  dueCount: number;
  milestones: Array<{ number: number; label: string; state: MilestoneState }>;
}

export interface ReferralDetail extends Omit<ReferralInfo, 'milestones'> {
  frequency: string;
  periodEndWeekday: number | null;
  ruleVersion: number | null;
  milestones: Array<Milestone & { number: number; dueDate: string | null; state: MilestoneState; claim: { lineId: string; recordId: string; periodEnd: string; amount: number; recordStatus: string } | null }>;
  events: Array<{ action: string; actorName: string; note: string | null; createdAt: string }>;
}

export type FundingState = 'NOT_APPLICABLE' | 'NOTHING_DUE' | 'UNFUNDED' | 'PARTIAL' | 'FUNDED' | 'OVER';
export const FUNDING_LABEL: Record<FundingState, string> = {
  NOT_APPLICABLE: '-', NOTHING_DUE: 'Nothing due', UNFUNDED: 'Customer has not paid', PARTIAL: 'Partly paid by customer', FUNDED: 'Paid by customer', OVER: 'Customer paid more',
};
export const fundingColor = (s: FundingState): 'default' | 'warning' | 'success' | 'error' | 'info' =>
  (s === 'FUNDED' ? 'success' : s === 'PARTIAL' ? 'warning' : s === 'UNFUNDED' ? 'default' : s === 'OVER' ? 'error' : 'info');

export interface FundingRecord {
  recordId: string;
  programName: string;
  periodStart: string;
  periodEnd: string;
  campaignName: string;
  concept: string | null;
  currency: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  due: number;
  funded: number;
  outstanding: number;
  state: FundingState;
}

export interface CustomerPayment {
  id: string;
  customerName: string;
  paymentDate: string;
  amount: number;
  currency: string;
  reference: string;
  note: string | null;
  status: 'ACTIVE' | 'VOIDED';
  recordedByName: string | null;
  voidReason: string | null;
  voidedByName: string | null;
  allocated: number;
  unallocated: number;
}

export interface CustomerPaymentDetail extends CustomerPayment {
  allocations: Array<{ id: string; recordId: string; amount: number; status: 'ACTIVE' | 'VOIDED'; createdByName: string | null; voidReason: string | null; periodEnd: string; programName: string }>;
}

export type PaymentState = 'NOT_SENT' | 'SENT' | 'SETTLED';
export type ReconStatus = 'OK' | 'UNVERIFIED' | 'MISMATCH' | 'MISSING' | 'CANCELLED';
export const RECON_LABEL: Record<ReconStatus, string> = { OK: 'Matches payroll', UNVERIFIED: 'Cannot compare', MISMATCH: 'Amount differs', MISSING: 'Not in payroll', CANCELLED: 'Cancelled in payroll' };
export const needsAttention = (s?: ReconStatus | null) => Boolean(s && s !== 'OK' && s !== 'UNVERIFIED');

export interface SettlementRow {
  lineId: string;
  recordId: string;
  employeeName: string;
  employeeNumber: string | null;
  programName: string;
  periodEnd: string;
  reference: string | null;
  amount: number;
  currency: string;
  paymentState: PaymentState;
  sentAt: string | null;
  payrollRef: string | null;
  settledAt: string | null;
  settlementRef: string | null;
  reconStatus: ReconStatus | null;
  reconNote: string | null;
  daysWaiting: number;
  isAdjustment: boolean;
  adjustments: number;
}

export interface AdjustmentInfo {
  id: string;
  code: string;
  kind: 'ADDITIONAL' | 'OVERPAYMENT';
  lineId: string;
  recordId: string;
  newRecordId: string | null;
  employeeName: string;
  amount: number;
  currency: string;
  reason: string;
  status: 'OPEN' | 'ADDED' | 'WITHDRAWN' | 'RECOVERED' | 'WAIVED';
  originalReference: string | null;
  originalPeriodEnd: string | null;
  programName: string | null;
  createdByName: string | null;
  createdAt: string;
  resolvedByName: string | null;
  resolutionNote: string | null;
}

export type HistoryClass = 'PENDING_RECONCILIATION' | 'PAID_HISTORICAL' | 'OWED' | 'NOT_OWED';
export const HISTORY_LABEL: Record<HistoryClass, string> = {
  PENDING_RECONCILIATION: 'Pending reconciliation', PAID_HISTORICAL: 'Paid before (history)', OWED: 'Owed', NOT_OWED: 'Not owed',
};

export interface HistoryRow {
  id: string;
  batchId: string;
  fileName: string | null;
  rowNumber: number;
  agentText: string | null;
  employeeId: string | null;
  employeeName: string | null;
  weekText: string | null;
  periodStart: string | null;
  periodEnd: string | null;
  amount: number | null;
  currency: string;
  concept: string | null;
  statusText: string | null;
  dealerPayText: string | null;
  teksPayText: string | null;
  notes: string | null;
  red: boolean;
  classification: HistoryClass;
  classifiedByName: string | null;
  classifiedAt: string | null;
  classificationNote: string | null;
  promotedLineId: string | null;
  promotedRecordId: string | null;
}

export interface HistoryRowDetail extends HistoryRow {
  raw: Record<string, unknown> | null;
  events: Array<{ action: string; actorName: string; note: string | null; createdAt: string }>;
}

export type ReportGroup = 'program' | 'campaign' | 'employee' | 'month' | 'week';
export interface ReportRow {
  key: string; label: string; currency: string; earnings: number; employees: number; total: number; approved: number; inApproval: number; rejected: number;
  readyForPayroll: number; sentNotPaid: number; paid: number; customerDue: number; customerReceived: number; customerOutstanding: number; heldCount: number; heldAmount: number; problems: number;
}
export interface ReportOverview {
  pendingReconciliation: { rows: number; total: number };
  eligibilityUnderReview: number;
  earningsHeldForEligibility: number;
  waitingForPayroll: { count: number; oldestDays: number };
  payrollCheckProblems: number;
  overpaymentsToRecover: { count: number; total: number };
  customerStillOwes: Record<string, number>;
  customerPaymentsNotMatched: Record<string, number>;
}
